'use client';

import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@components/ui/button';
import type { SearchHit } from '../../search/types';

export interface KnowledgeSearchAnswerCardProps {
  answerText: string;
  citedHitIds: string[];
  results: SearchHit[];
  onCitationClick: (hitId: string) => void;
  /** Omitted (e.g. `agents-membership` off) hides "Ask about this" entirely. */
  onAskAboutThis?: () => void;
}

/**
 * Synthesized, cited answer shown above the row list for `intent: 'question'`
 * (plan Step 5.2). Citation chips are numbered in the order the model cited
 * them; each chip resolves against `results` for a title and, on click,
 * scrolls to/highlights that row (`KnowledgeSearch`/`KnowledgeSearchMobile`
 * own the actual scroll/highlight state -- this component only reports which
 * hit was clicked). A citation id that doesn't resolve to a hit in `results`
 * is dropped rather than rendered as a dead chip -- `synthesizeAnswer`
 * already strips hallucinated ids, but a hit can also legitimately fall out
 * of `results` (e.g. truncated to the limit) after synthesis ran on the
 * fuller evidence set.
 */
const KnowledgeSearchAnswerCard: FC<KnowledgeSearchAnswerCardProps> = ({
  answerText,
  citedHitIds,
  results,
  onCitationClick,
  onAskAboutThis
}) => {
  const t = useTranslations('knowledge-search');
  const hitsById = new Map(results.map(hit => [hit.id, hit]));
  const citations = citedHitIds
    .map(hitId => hitsById.get(hitId))
    .filter((hit): hit is SearchHit => hit !== undefined);

  return (
    <div className="mx-4 mt-3 rounded-xl bg-card p-4">
      <p className="text-body text-foreground">{answerText}</p>
      {citations.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-caption text-muted-foreground">
            {t('answerSourcesLabel')}
          </span>
          {citations.map((hit, index) => (
            <button
              key={hit.id}
              type="button"
              onClick={() => onCitationClick(hit.id)}
              className="max-w-[220px] truncate rounded-sm bg-foreground/5 px-1.5 py-0.5 text-meta font-medium text-muted-foreground"
              title={hit.title}
            >
              {index + 1}. {hit.title}
            </button>
          ))}
        </div>
      ) : null}
      {onAskAboutThis ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-8 px-2 text-caption"
          onClick={onAskAboutThis}
        >
          {t('askAboutThis')}
        </Button>
      ) : null}
    </div>
  );
};

export default KnowledgeSearchAnswerCard;
